/*
 * Copyright (c) New Cloud Technologies, Ltd., 2013-2022
 *
 * You can not use the contents of the file in any way without New Cloud Technologies, Ltd. written permission.
 * To obtain such a permit, you should contact New Cloud Technologies, Ltd. at http://ncloudtech.com/contact.html
 *
 */

import * as Comlink from 'comlink';

import { NotifyPayload, ErrorPayload, SaveEndPayload, ReadyPayload, UrlPayload, WopiActions } from './types';
import { EVENTS } from './constants';

type SubscribeSet<T extends NotifyPayload> = Set<(response: T) => void>;

export class WopiApi {
    private subscribes = new Map<EVENTS, SubscribeSet<NotifyPayload>>();
    private readonly internalApi: Comlink.Remote<WopiActions>;

    constructor(windowObject: Window, origin: string) {
        const windowEndpoint = Comlink.windowEndpoint(windowObject, self, origin);
        this.internalApi = Comlink.wrap<WopiActions>(windowEndpoint);

        Comlink.expose(this.invoke.bind(this), windowEndpoint);
    }

    onInit(callback: () => void) {
        return this.subscribe(EVENTS.init, callback);
    }

    onReady(callback: (payload: ReadyPayload) => void) {
        return this.subscribe(EVENTS.ready, callback);
    }

    onError(callback: (payload: ErrorPayload) => void) {
        return this.subscribe(EVENTS.error, callback);
    }

    onSaveStart(callback: () => void) {
        return this.subscribe(EVENTS.saveStart, callback);
    }

    onSaveEnd(callback: (payload: SaveEndPayload) => void) {
        return this.subscribe(EVENTS.saveEnd, callback);
    }

    onReloadAction(callback: () => void) {
        return this.subscribe(EVENTS.reload, callback);
    }

    onFallbackAction(callback: () => void) {
        return this.subscribe(EVENTS.fallback, callback);
    }

    blurAction() {
        return this.internalApi.blur();
    }

    onRedirectAction(callback: (payload: UrlPayload) => void) {
        return this.subscribe(EVENTS.redirect, callback);
    }

    async saveAction() {
        await this.internalApi.saveByRequest();
    }

    private invoke<T extends NotifyPayload>(action: EVENTS, response?: T) {
        const subscribes = this.subscribes.get(action);
        if (subscribes) {
            subscribes.forEach((cb) => cb(response));
        }
    }

    private subscribe<T extends NotifyPayload>(notification: EVENTS, callback: (payload: T) => void) {
        let subscribes = this.subscribes.get(notification);
        if (!subscribes) {
            subscribes = new Set();
            this.subscribes.set(notification, subscribes);
        }

        // @ts-expect-error weak type, fix
        subscribes.add(callback);

        // unsubscribe
        return () => {
            const subscribes = this.subscribes.get(notification);
            if (!subscribes) {
                return;
            }

            // @ts-expect-error weak type, fix
            subscribes.delete(callback);
        };
    }
}

export default WopiApi;
